import { RefreshCw } from "lucide-react";
import { type QuizConfig } from "~/types/quiz";

export const testOcd: QuizConfig = {
  id: "test-ocd",
  illustration: {
    src: "/images/quizzes/test-ocd.webp",
    alt: "Žena sa ključevima zastaje i ponovo gleda zaključana vrata.",
  },
  lastModified: "2026-10-05",
  title: "Test OCD: Proveri simptome opsesivno-kompulzivnog poremećaja",
  shortTitle: "Test OCD",
  description:
    "Besplatan test OCD simptoma - otkrij da li tvoje opsesivne misli i kompulzivna ponašanja ukazuju na potrebu za stručnom pomoći.",
  longDescription:
    "Ovaj test analizira tvoje obrasce razmišljanja i ponašanja - od neželjenih misli koje se vraćaju, preko potrebe za proveravanjem i sređivanjem, do ritualnih radnji koje ti donose privremeni mir. Saznaj koliko ovi obrasci utiču na tvoj svakodnevni život i kada je pravo vreme da potražiš podršku stručnjaka. Test je inspirisan OCI-R skalom i služi kao samoprocena, ne kao dijagnoza.",
  icon: RefreshCw,
  duration: "4-6 min",
  category: "Mentalno zdravlje",
  focusAreasWanted: [
    "ocd",
    "anxiety",
    "emotional_regulation",
    "self_development",
  ],
  featured: false,
  questions: [
    // Demographic
    {
      type: "radio",
      question: "Pol:",
      options: ["Muški", "Ženski", "Ne želim da se izjasnim"],
      scores: [0, 0, 0],
    },
    // Checking dimension (OCI-R items 2, 8, 14)
    {
      type: "radio",
      question:
        "Koliko često osećaš potrebu da više puta proveriš stvari (brave, šporet, slavine)?",
      options: [
        "Nikad",
        "Retko",
        "Ponekad",
        "Često",
        "Skoro uvek",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question:
        "Da li se vraćaš da ponovo proveriš nešto iako znaš da si već proverio/la?",
      options: [
        "Ne, nikad",
        "Retko, možda jednom",
        "Ponekad, dva-tri puta",
        "Često, više puta",
        "Stalno, ne mogu da prestanem",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    // Washing/Contamination dimension (OCI-R items 5, 11, 17)
    {
      type: "radio",
      question:
        "Koliko te uznemirava dodir sa predmetima koje su drugi ljudi dodirnuli?",
      options: [
        "Uopšte me ne uznemirava",
        "Malo me uznemirava",
        "Umereno me uznemirava",
        "Dosta me uznemirava",
        "Izuzetno me uznemirava",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question: "Koliko često pereš ruke ili se tuširaš više nego što je potrebno?",
      options: [
        "Nikad",
        "Retko",
        "Ponekad",
        "Često",
        "Veoma često",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    // Ordering/Symmetry dimension (OCI-R items 3, 9, 15)
    {
      type: "radio",
      question:
        "Koliko ti je važno da stvari budu raspoređene na određen, tačan način?",
      options: [
        "Nije mi važno",
        "Malo mi je važno",
        "Umereno mi je važno",
        "Dosta mi je važno",
        "Izuzetno mi je važno",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question:
        "Da li osećaš nelagodu ako stvari nisu savršeno poređane ili simetrične?",
      options: [
        "Ne, ne smeta mi",
        "Malo mi smeta",
        "Umereno mi smeta",
        "Dosta mi smeta",
        "Jako mi smeta, moram da sredim",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    // Obsessing dimension (OCI-R items 6, 12, 18)
    {
      type: "radio",
      question:
        "Koliko često ti se vraćaju neprijatne misli koje ne možeš da zaustaviš?",
      options: [
        "Nikad",
        "Retko",
        "Ponekad",
        "Često",
        "Skoro stalno",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question:
        "Da li ti neželjene misli stvaraju značajan stres ili anksioznost?",
      options: [
        "Ne, nikakav stres",
        "Minimalan stres",
        "Umeren stres",
        "Značajan stres",
        "Intenzivan stres",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    // Neutralizing dimension (OCI-R items 4, 10, 16)
    {
      type: "radio",
      question:
        "Da li osećaš potrebu da brojiš, ponavljaš reči ili radiš mentalne rituale da bi se osećao/la bolje?",
      options: [
        "Nikad",
        "Retko",
        "Ponekad",
        "Često",
        "Skoro uvek",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question:
        "Da li moraš da uradiš nešto na određeni način ili određeni broj puta da bi se osećao/la 'u redu'?",
      options: [
        "Ne, nikad",
        "Retko",
        "Ponekad",
        "Često",
        "Skoro uvek",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    // Impact question
    {
      type: "radio",
      question:
        "Koliko ovi obrasci utiču na tvoj svakodnevni život (posao, odnose, slobodno vreme)?",
      options: [
        "Ne utiču uopšte",
        "Minimalno utiču",
        "Umereno utiču",
        "Značajno utiču",
        "Jako ometaju moj život",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    // Common obsessive themes
    {
      type: "checkbox",
      checkboxScoring: "sum",
      question:
        "Koje vrste neželjenih misli ili briga te najviše muče? (označi sve što važi)",
      options: [
        "Strah od kontaminacije ili zaraze",
        "Strah da ću povrediti sebe ili druge",
        "Strah da nisam zaključao/la ili isključio/la nešto",
        "Potreba da stvari budu savršene ili simetrične",
        "Neprijatne seksualne ili nasilne misli",
        "Strah od gubitka kontrole",
        "Religiozne ili moralne brige",
        "Ništa od navedenog",
      ],
      scores: [1, 1, 1, 1, 1, 1, 1, 0],
    },
    // Coping
    {
      type: "checkbox",
      checkboxScoring: "context",
      question: "Šta ti pomaže kada se pojave ove misli ili ponašanja? (označi sve što važi)",
      options: [
        "Razgovor sa bliskom osobom",
        "Fizička aktivnost",
        "Tehnike opuštanja ili disanja",
        "Odvraćanje pažnje (TV, muzika, čitanje)",
        "Prihvatanje misli bez reagovanja",
        "Ništa mi posebno ne pomaže",
      ],
      scores: [1, 1, 1, 1, 1, 0],
    },
    // Textarea
    {
      type: "textarea",
      question:
        "Opiši kako ovi obrasci utiču na tvoj život ili šta te najviše brine:",
    },
  ],
  copy: {
    title: "Tvoja procena OCD simptoma",
    subtitle: "Razumevanje tvojih opsesivnih i kompulzivnih obrazaca",
    scoreLabel: "Tvoj rezultat:",
    adviceTitle: "Preporuke za tebe",
    resourcesTitle: "Korisni resursi",
    whatNextTitle: "Šta dalje?",
    shareButtonText: "Podeli rezultat",
  },
};
